import React from 'react';
import type { FacultyMember } from '../../types/faculty';
import { facultyLinkClass } from './facultyUiStyles';

interface FacultyOverviewTabProps {
  faculty: FacultyMember;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatPhone = (phone?: string) => phone || '—';

export function FacultyOverviewTab({ faculty }: FacultyOverviewTabProps) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Identity information</h3>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Legal name</p>
            <p className="text-sm font-medium text-iron-grey">
              {faculty.firstName} {faculty.lastName}
            </p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Hebrew name</p>
            <p className="text-sm text-iron-grey" dir="rtl">
              {[faculty.hebrewFirstName, faculty.hebrewLastName].filter(Boolean).join(' ') || '—'}
            </p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Preferred name</p>
            <p className="text-sm text-iron-grey">{faculty.preferredName || '—'}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Gender</p>
            <p className="text-sm text-iron-grey">{faculty.gender || '—'}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Date of birth</p>
            <p className="text-sm text-iron-grey">{formatDate(faculty.dateOfBirth)}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Contact information</h3>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Work email</p>
            <a href={`mailto:${faculty.workEmail}`} className={facultyLinkClass}>
              {faculty.workEmail}
            </a>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Personal email</p>
            {faculty.personalEmail ? (
              <a href={`mailto:${faculty.personalEmail}`} className={facultyLinkClass}>
                {faculty.personalEmail}
              </a>
            ) : (
              <p className="text-sm text-iron-grey">—</p>
            )}
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Cell phone</p>
            <p className="text-sm text-iron-grey">{formatPhone(faculty.cellPhone)}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Home phone</p>
            <p className="text-sm text-iron-grey">{formatPhone(faculty.homePhone)}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Home address</p>
            <p className="text-sm text-iron-grey">
              {faculty.address ? (
                <>
                  {faculty.address.street}
                  <br />
                  {faculty.address.city}, {faculty.address.state} {faculty.address.zip}
                </>
              ) : (
                '—'
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Employment details</h3>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Employee ID</p>
            <p className="text-sm text-iron-grey">{faculty.employeeId || '—'}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Hire date</p>
            <p className="text-sm text-iron-grey">{formatDate(faculty.hireDate)}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Position</p>
            <p className="text-sm text-iron-grey">{faculty.position || '—'}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Employment type</p>
            <p className="text-sm text-iron-grey">{faculty.employmentType || '—'}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Division(s)</p>
            <p className="text-sm text-iron-grey">{faculty.divisions.join(', ') || '—'}</p>
          </div>
          <div>
            <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Reports to</p>
            <p className={`${facultyLinkClass} cursor-pointer`}>{faculty.reportsTo || '—'}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Emergency contact</h3>
        {faculty.emergencyContact ? (
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Contact name</p>
              <p className="text-sm font-medium text-iron-grey">{faculty.emergencyContact.name}</p>
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Relationship</p>
              <p className="text-sm text-iron-grey">{faculty.emergencyContact.relationship}</p>
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Primary phone</p>
              <p className="text-sm text-iron-grey">{faculty.emergencyContact.phone}</p>
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Alternate phone</p>
              <p className="text-sm text-iron-grey">{formatPhone(faculty.emergencyContact.alternatePhone)}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-text-secondary">No emergency contact on file.</p>
        )}
      </div>
    </div>
  );
}
